import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function SmoothScroll({ children }) {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 2.0, // Increased from 1.2 for slower, smoother scroll
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 0.6, // Reduced from 1 for less scroll per wheel movement
      smoothTouch: false,
      touchMultiplier: 1.5, // Reduced from 2 for slower touch scrolling
      infinite: false,
    });

    lenisRef.current = lenis;

    // Connect Lenis to GSAP ScrollTrigger
    function raf(time) {
      lenis.raf(time);
      ScrollTrigger.update();
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Update ScrollTrigger when Lenis scrolls
    lenis.on("scroll", ScrollTrigger.update);

    // Recalculate scroll height when content changes
    const recalculateHeight = () => {
      lenis.resize();
      ScrollTrigger.refresh();
    };

    // Recalculate on window resize and after a delay to catch dynamic content
    window.addEventListener("resize", recalculateHeight);
    const timeoutId = setTimeout(recalculateHeight, 100);
    const intervalId = setInterval(recalculateHeight, 1000); // Periodic refresh

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      window.removeEventListener("resize", recalculateHeight);
      lenis.destroy();
      // Note: We don't kill ScrollTrigger instances here because other components
      // (like Header) create their own ScrollTrigger instances that should persist
      // We only refresh ScrollTrigger to ensure it's in sync with Lenis
      ScrollTrigger.refresh();
    };
  }, []);

  // Scroll to top when route changes
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: false });
    }
  }, [location.pathname]);

  return <>{children}</>;
}
